import React, { useState, useEffect } from 'react';
import { pastEvents } from '../data/mock';
import { Calendar as CalendarIcon, Clock, Users, CheckCircle, AlertCircle } from 'lucide-react';
import { PartyInquiryDialog } from '../components/PartyInquiryDialog';
import { TeamInquiryDialog } from '../components/TeamInquiryDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Business Hours Constants
const WEEKDAY_START_HOUR = 11;  // 11:00 AM
const WEEKEND_START_HOUR = 9;   // 9:00 AM
const BUSINESS_END_HOUR = 23;   // 11:00 PM

// Helper function to parse date strings as local dates (avoids timezone issues)
const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Session prices per person: duration -> { adult, child }
const SESSION_PRICES = {
  '30min': { adult: 35, child: 25 },
  '1hour': { adult: 50, child: 40 },
};

export const Events = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null); // '30min' or '1hour'
  const [isBooking, setIsBooking] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bookingConfirmation, setBookingConfirmation] = useState(null);
  const [dateBookings, setDateBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [showCustomBookingDialog, setShowCustomBookingDialog] = useState(false);
  const [showPartyDialog, setShowPartyDialog] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [eventDialogView, setEventDialogView] = useState('announcement'); // 'announcement' or 'booking'
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    adults: 1,
    children: 0,
    duration: '30min',
    special_requests: ''
  });
  
  // Fetch upcoming events from the database (managed via Admin dashboard)
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events`);
        if (response.data.success) {
          setUpcomingEvents(response.data.data || []);
        }
      } catch (error) {
        setUpcomingEvents([]);
      }
    };
    fetchEvents();
  }, []);
  
  // Find event for selected date
  const selectedDateEvent = upcomingEvents.find(event => {
    if (!event.date) return false;
    const eventDate = parseLocalDate(event.date);
    return eventDate.toDateString() === selectedDate.toDateString();
  });
  
  // Removed automatic popup for past events
  // Event popups are no longer shown automatically
  
  // Fetch bookings for selected date to block booked time slots
  useEffect(() => {
    const fetchBookingsForDate = async () => {
      if (!selectedDate) return;
      
      setLoadingBookings(true);
      try {
        // Fetch all bookings
        const response = await axios.get(`${API_URL}/api/bookings/public`);
        if (response.data.success) {
          const dateStr = selectedDate.toLocaleDateString();
          const bookingsForDate = response.data.data.filter(
            booking => booking.event_date === dateStr
          );
          setDateBookings(bookingsForDate);
        }
      } catch (error) {
        // If endpoint doesn't exist yet, just continue
        setDateBookings([]);
      } finally {
        setLoadingBookings(false);
      }
    };
    
    fetchBookingsForDate();
  }, [selectedDate, API_URL]);
  
  // Generate available time slots based on business hours
  const isDayBlocked = dateBookings.some(booking => booking.event_time === 'ALL DAY');
  
  const generateTimeSlots = (date) => {
    const slots = [];
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Business hours: Weekday 11:00 AM - 11:00 PM, Weekend 9:00 AM - 11:00 PM
    const startHour = isWeekend ? WEEKEND_START_HOUR : WEEKDAY_START_HOUR;
    const endHour = BUSINESS_END_HOUR;
    
    for (let hour = startHour; hour < endHour; hour++) {
      // Generate slots on the hour and half hour
      ['00', '30'].forEach(minutes => {
        const time = `${hour > 12 ? hour - 12 : hour === 0 ? 12 : hour}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
        
        // Check if this time slot is already booked
        const isBooked = dateBookings.some(booking => 
          booking.event_time === time
        );
        
        slots.push({
          time,
          available: !isBooked && !isDayBlocked // Mark as unavailable if booked or day is blocked
        });
      });
    }
    
    return slots;
  };
  
  const availableTimeSlots = generateTimeSlots(selectedDate);

  const handleBookingSubmit = async () => {
    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!selectedTimeSlot) {
      toast.error('Please select a time slot');
      return;
    }
    
    if (!selectedDuration) {
      toast.error('Please select a session duration');
      return;
    }
    
    if (bookingData.adults + bookingData.children < 1) {
      toast.error('Please enter at least one participant');
      return;
    }
    
    setIsBooking(true);
    
    try {
      const payload = {
        event_id: `booking_${Date.now()}`,
        event_title: `${selectedDuration === '30min' ? '30 Minute' : '1 Hour'} Session`,
        event_date: selectedDate.toLocaleDateString(),
        event_time: selectedTimeSlot,
        customer_name: bookingData.customer_name,
        customer_email: bookingData.customer_email,
        customer_phone: bookingData.customer_phone,
        participants: bookingData.adults + bookingData.children,
        adults: bookingData.adults,
        children: bookingData.children,
        duration: selectedDuration === '30min' ? '30 Minutes' : '1 Hour',
        special_requests: bookingData.special_requests
      };
      
      const response = await axios.post(`${API_URL}/api/bookings`, payload);
      
      if (response.data.success) {
        // Show success modal instead of toast
        setBookingConfirmation({
          eventTitle: payload.event_title,
          date: selectedDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          time: selectedTimeSlot,
          duration: payload.duration,
          participants: `${bookingData.adults + bookingData.children} (${bookingData.adults} Adult${bookingData.adults !== 1 ? 's' : ''}, ${bookingData.children} Child${bookingData.children !== 1 ? 'ren' : ''})`,
          email: bookingData.customer_email,
          confirmationNumber: response.data.confirmation_number,
          estimatedTotal: response.data.estimated_total
        });
        setShowSuccessModal(true);
        
        // Reset booking data and close dialog
        setBookingData({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          adults: 1,
    children: 0,
          duration: '30min',
          special_requests: ''
        });
        setSelectedTimeSlot(null);
        setSelectedDuration(null);
        setSelectedEvent(null);
      } else {
        toast.error('Booking failed', {
          description: response.data.message || 'Please try again later.'
        });
      }
    } catch (error) {
      toast.error('Booking failed', {
        description: 'Please try again later or contact us directly.'
      });
    } finally {
      setIsBooking(false);
    }
  };
  
  const handleDurationSelect = (duration) => {
    // Validate that date and time are selected
    if (!selectedDate) {
      toast.error('Please select a date first');
      return;
    }
    if (!selectedTimeSlot) {
      toast.error('Please select a time slot first');
      return;
    }
    
    setSelectedDuration(duration);
    setShowCustomBookingDialog(true); // Open custom booking dialog
  };

  // Get all event dates for highlighting in calendar
  const eventDates = upcomingEvents
    .filter(event => event.date)
    .map(event => parseLocalDate(event.date));

  return (
    <div className="min-h-screen pt-24 pb-16 bg-white">
      {/* Header with Session Options */}
      <section className="px-6 py-8 bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 border-b-4 border-yellow-300">
        <div className="container mx-auto max-w-5xl">
          <h1 className="text-5xl font-black gold-shimmer mb-2">Book Your Session</h1>
          <p className="text-lg text-gray-700 mb-6">
            Choose your creative experience at Rage Room with Paint
          </p>
          
          {/* Session Options - Now Interactive as Step 1 */}
          <div className="bg-white border-3 border-yellow-400 rounded-xl p-6 shadow-xl mb-6">
            <h3 className="font-black shimmer-heading text-2xl mb-2 text-center">Step 1: Select Your Session Type</h3>
            <p className="text-center text-gray-700 mb-5 text-sm">Click on a session type to begin your booking</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button 
                onClick={() => setShowPartyDialog(true)}
                data-testid="session-type-birthday"
                className="bg-gradient-to-br from-pink-600 to-purple-600 p-5 rounded-xl border-3 border-pink-400 shadow-lg hover:shadow-pink-500/50 hover:scale-105 transition-all cursor-pointer text-left"
              >
                <h4 className="font-black text-white mb-2 text-base">🎉 Birthday Party Packages</h4>
                <p className="text-sm text-pink-100 font-medium">Perfect for ALL ages - because the best birthday memories are made outside the lines. Quick, colorful fun with zero rules. Just pure creative release! Packages starting as low as $120 with all materials included.</p>
                <p className="text-xs text-white font-bold mt-2 underline">Tap to request a party →</p>
              </button>
              
              <button 
                onClick={() => setShowTeamDialog(true)}
                data-testid="session-type-team-building"
                className="bg-gradient-to-br from-blue-600 to-sky-600 p-5 rounded-xl border-3 border-blue-400 shadow-lg hover:shadow-blue-500/50 hover:scale-105 transition-all cursor-pointer text-left"
              >
                <h4 className="font-black text-white mb-2 text-base">🤝 Team Building Sessions</h4>
                <p className="text-sm text-blue-100 font-medium">Team building with a splash. Energizing, messy, and surprisingly meaningful. For teams who want to reconnect, reset, and actually laugh together</p>
                <p className="text-xs text-white font-bold mt-2 underline">Tap to request a team event →</p>
              </button>
              
              <button 
                onClick={() => {
                  toast.success('👤 Solo Studio Selected!', {
                    description: 'Now select your date below (Step 2)'
                  });
                  document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="session-type-solo-studio"
                className="bg-gradient-to-br from-purple-600 to-violet-600 p-5 rounded-xl border-3 border-purple-400 shadow-lg hover:shadow-purple-500/50 hover:scale-105 transition-all cursor-pointer text-left"
              >
                <h4 className="font-black text-white mb-2 text-base">👤 Solo Studio</h4>
                <p className="text-sm text-purple-100 font-medium">Take a little time for yourself and step into your own creative space. Choose your colors, turn up the music, and let the paint fly. No group, no pressure, no rules - Just you creating something completely your own</p>
              </button>
              
              <button 
                onClick={() => {
                  toast.success('👥 Group Session Selected!', {
                    description: 'Now select your date below (Step 2)'
                  });
                  document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-br from-yellow-500 to-amber-600 p-5 rounded-xl border-3 border-yellow-400 shadow-lg hover:shadow-yellow-500/50 hover:scale-105 transition-all cursor-pointer text-left"
              >
                <h4 className="font-black text-white mb-2 text-base">👥 Group Sessions</h4>
                <p className="text-sm text-amber-100 font-medium">Bring friends & family for a fun creative experience together! Minimum of 4 people required for the group rate discount to be applied.</p>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Instructions + Calendar Side by Side */}
      <section id="calendar-section" className="px-6 py-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            
            {/* Left: Instructions */}
            <div className="bg-gradient-to-br from-pink-50 to-yellow-50 p-4 rounded-xl shadow-md border-2 border-pink-200 h-fit">
              <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <span className="text-xl">📅</span> How to Book
              </h3>
              <div className="space-y-2.5">
                <div className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-7 h-7 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xs">1</span>
                  <p className="text-gray-700 text-sm pt-0.5"><strong>Pick a day</strong> - Choose date from calendar</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-7 h-7 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xs">2</span>
                  <p className="text-gray-700 text-sm pt-0.5"><strong>Select time</strong> - Pick from available slots</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-7 h-7 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xs">3</span>
                  <p className="text-gray-700 text-sm pt-0.5"><strong>Choose duration</strong> - 30-min or 1-hour</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="flex-shrink-0 w-7 h-7 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-xs">4</span>
                  <p className="text-gray-700 text-sm pt-0.5"><strong>Complete booking</strong> - Fill details & confirm</p>
                </div>
              </div>
            </div>

            {/* Right: Calendar + Event/Booking Display */}
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-xl shadow-md border-2 border-blue-200">
                <h3 className="text-xl font-bold mb-4 text-gray-900">Select Date</h3>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-lg border-0"
                />
                <div className="mt-4 p-3 bg-gradient-to-br from-pink-50 to-yellow-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Selected Date:</p>
                  <p className="text-base font-semibold text-gray-900">
                    {selectedDate?.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              
              {/* Event/Booking Info Panel */}
              {selectedDateEvent ? (
                <div className="bg-gradient-to-br from-pink-100 via-yellow-100 to-blue-100 p-4 rounded-xl border-2 border-pink-400 animate-in slide-in-from-right">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-pink-600" />
                    <h4 className="text-lg font-bold text-gray-900">Event on This Date!</h4>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <h5 className="font-bold text-pink-600 mb-2">{selectedDateEvent.title}</h5>
                    <p className="text-sm text-gray-700 mb-2">{selectedDateEvent.description}</p>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-yellow-600" />
                        <span className="text-gray-600">{selectedDateEvent.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-pink-600" />
                        <span className="text-gray-600">{selectedDateEvent.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-gray-600">{selectedDateEvent.spotsAvailable} spots available</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarIcon className="w-5 h-5 text-blue-600" />
                    <h4 className="text-base font-bold text-gray-900">Bookings for This Date</h4>
                  </div>
                  {loadingBookings ? (
                    <p className="text-sm text-gray-600">Loading bookings...</p>
                  ) : dateBookings.length > 0 ? (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {dateBookings.map((booking) => (
                        <div key={booking.id || booking._id || `${booking.event_title}-${booking.event_time}`} className="bg-white p-2 rounded-lg text-xs">
                          <p className="font-semibold text-gray-900">{booking.event_title}</p>
                          <p className="text-gray-600">{booking.event_time}</p>
                          {booking.status !== 'blocked' && (
                            <p className="text-gray-500">{booking.participants} participant(s)</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">No bookings yet for this date</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Available Time Slots - Dropdown Selection */}
          <div className="max-w-5xl mx-auto mt-6">
            <div className="bg-white border-2 border-purple-300 p-6 rounded-xl shadow-xl">
              <h4 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-500" />
                Available Time Slots
              </h4>
              {isDayBlocked && (
                <p className="text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-3" data-testid="day-blocked-notice">
                  🚫 This day is unavailable. Please pick another date.
                </p>
              )}
              <Select value={selectedTimeSlot || ""} onValueChange={(value) => {
                setSelectedTimeSlot(value);
                toast.info('Great! Now scroll down to select your session length (Step 3)');
              }}>
                <SelectTrigger className="w-full text-lg py-6" data-testid="time-slot-select">
                  <SelectValue placeholder="Select a time slot" />
                </SelectTrigger>
                <SelectContent>
                  {availableTimeSlots.map((slot) => (
                    <SelectItem 
                      key={slot.time} 
                      value={slot.time}
                      disabled={!slot.available}
                      className={`text-base py-3 ${
                        !slot.available 
                          ? 'opacity-50 line-through cursor-not-allowed' 
                          : 'hover:bg-pink-50'
                      }`}
                    >
                      {slot.time} {!slot.available && '(Booked)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-3 text-center">
                💡 Crossed-out times are already booked
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Step 3 */}
      <section className="px-6 py-8">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto bg-white border-2 border-pink-300 rounded-xl shadow-xl p-6">
            <div className="text-center mb-5">
              <h2 className="text-2xl font-bold shimmer-heading mb-1.5">Step 3: Select Session Length</h2>
              <p className="text-sm text-gray-600">Choose your session duration to complete booking</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => handleDurationSelect('30min')}
                className="bg-gradient-to-br from-pink-600 to-purple-600 p-4 rounded-lg border-2 border-pink-400 hover:border-pink-300 hover:shadow-lg hover:shadow-pink-500/50 transition-all duration-300 cursor-pointer text-left"
              >
                <h3 className="font-bold text-base text-white mb-2">30 Minute Session</h3>
                <p className="text-pink-100 text-sm mb-0.5">Children: <span className="font-bold text-white text-lg">$25</span></p>
                <p className="text-pink-100 text-sm">Adults: <span className="font-bold text-white text-lg">$35</span></p>
                <p className="text-xs text-pink-200 font-semibold mt-2">Click to book →</p>
              </button>
              <button
                onClick={() => handleDurationSelect('1hour')}
                className="bg-gradient-to-br from-sky-600 to-blue-600 p-4 rounded-lg border-2 border-sky-400 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-500/50 transition-all duration-300 cursor-pointer text-left"
              >
                <h3 className="font-bold text-base text-white mb-2">1 Hour Session</h3>
                <p className="text-sky-100 text-sm mb-0.5">Children: <span className="font-bold text-white text-lg">$40</span></p>
                <p className="text-sky-100 text-sm">Adults: <span className="font-bold text-white text-lg">$50</span></p>
                <p className="text-xs text-sky-200 font-semibold mt-2">Click to book →</p>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="px-6 py-8 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Upcoming Sessions</h3>
          {upcomingEvents.length === 0 && (
            <p className="text-gray-600 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4" data-testid="no-upcoming-sessions">
              No special events scheduled right now - but you can book your own session above anytime!
            </p>
          )}
          <div className="space-y-4">
            {upcomingEvents.map((event) => (
                  <div 
                    key={event.id}
                    className={`bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 ${
                      event.isPopup 
                        ? 'border-3 border-pink-400 bg-gradient-to-br from-pink-50 via-yellow-50 to-blue-50' 
                        : 'border-l-4 border-pink-400'
                    }`}
                  >
                    {event.isPopup && (
                      <div className="mb-3">
                        <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-pink-500 to-yellow-500 text-white text-xs font-bold rounded-full animate-pulse">
                          🎉 FIRST POP-UP EVENT! 🎉
                        </span>
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-start gap-2.5 mb-2.5">
                          <div className={`w-10 h-10 ${
                            event.isPopup 
                              ? 'bg-gradient-to-br from-pink-500 to-yellow-500' 
                              : 'bg-gradient-to-br from-pink-400 to-yellow-400'
                          } rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <CalendarIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className={`${
                              event.isPopup ? 'text-xl' : 'text-lg'
                            } font-bold text-gray-900 mb-0.5`}>
                              {event.title}
                            </h4>
                            <p className="text-gray-600 text-xs">{event.description}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                          {event.date ? (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <CalendarIcon className="w-3.5 h-3.5 text-pink-500" />
                              <span>{parseLocalDate(event.date).toLocaleDateString()}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                              <CalendarIcon className="w-3.5 h-3.5 text-amber-500" />
                              <span>Date TBD</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Clock className="w-3.5 h-3.5 text-yellow-500" />
                            <span>{event.time}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Users className="w-3.5 h-3.5 text-blue-500" />
                            <span>
                              {event.spotsAvailable === 'TBD' ? 'Contact us' : `${event.spotsAvailable} spots`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-600">
                            <Clock className="w-3.5 h-3.5 text-pink-500" />
                            <span>{event.duration}</span>
                          </div>
                        </div>
                      </div>

                      {event.date && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              className={`${
                                event.isPopup
                                  ? 'bg-gradient-to-r from-pink-500 to-yellow-500 hover:from-pink-600 hover:to-yellow-600 text-white font-bold px-6 py-2.5 text-base'
                                  : 'bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 text-white font-semibold px-5 py-2 text-sm'
                              } rounded-lg`}
                              onClick={() => setSelectedEvent(event)}
                            >
                              Book Now
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md" aria-describedby="booking-dialog-description">
                            <DialogHeader>
                              <DialogTitle>Book {selectedEvent?.title}</DialogTitle>
                              <DialogDescription id="booking-dialog-description">
                                Fill in your details to confirm your booking
                              </DialogDescription>
                            </DialogHeader>
                            {selectedEvent && (
                              <div className="space-y-4 py-4">
                                <div className="p-4 bg-gradient-to-br from-pink-50 to-yellow-50 rounded-xl">
                                  <h4 className="font-semibold text-gray-900 mb-2">{selectedEvent.title}</h4>
                                  <div className="space-y-2 text-sm text-gray-600">
                                    <p className="flex items-center gap-2">
                                      <CalendarIcon className="w-4 h-4" />
                                      {parseLocalDate(selectedEvent.date).toLocaleDateString()}
                                    </p>
                                    <p className="flex items-center gap-2">
                                      <Clock className="w-4 h-4" />
                                      {selectedEvent.time} - {selectedEvent.duration}
                                    </p>
                                    <p className="flex items-center gap-2">
                                      <Users className="w-4 h-4" />
                                      {selectedEvent.spotsAvailable} spots available
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Full Name *
                                    </label>
                                    <Input
                                      type="text"
                                      placeholder="Your name"
                                      value={bookingData.customer_name}
                                      onChange={(e) => setBookingData({...bookingData, customer_name: e.target.value})}
                                      required
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Email *
                                    </label>
                                    <Input
                                      type="email"
                                      placeholder="your.email@example.com"
                                      value={bookingData.customer_email}
                                      onChange={(e) => setBookingData({...bookingData, customer_email: e.target.value})}
                                      required
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Phone *
                                    </label>
                                    <Input
                                      type="tel"
                                      placeholder="(123) 456-7890"
                                      value={bookingData.customer_phone}
                                      onChange={(e) => setBookingData({...bookingData, customer_phone: e.target.value})}
                                      required
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Number of Participants *
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Adults</label>
                                        <Input
                                          type="number"
                                          min="0"
                                          data-testid="event-booking-adults-input"
                                          value={bookingData.adults}
                                          onChange={(e) => setBookingData({...bookingData, adults: Math.max(0, parseInt(e.target.value) || 0)})}
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Children</label>
                                        <Input
                                          type="number"
                                          min="0"
                                          data-testid="event-booking-children-input"
                                          value={bookingData.children}
                                          onChange={(e) => setBookingData({...bookingData, children: Math.max(0, parseInt(e.target.value) || 0)})}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Session Duration *
                                    </label>
                                    <select
                                      value={bookingData.duration}
                                      onChange={(e) => setBookingData({...bookingData, duration: e.target.value})}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                                      required
                                    >
                                      <option value="30min">30 Minutes</option>
                                      <option value="1hour">1 Hour</option>
                                    </select>
                                  </div>
                                  
                                  {SESSION_PRICES[bookingData.duration] && (
                                    <div className="bg-gradient-to-r from-yellow-50 to-pink-50 border border-pink-200 rounded-lg p-3" data-testid="event-booking-estimated-total">
                                      <p className="text-sm text-gray-900">
                                        <strong>Estimated Total:</strong>{' '}
                                        <span className="font-black text-pink-600 text-lg">
                                          ${(bookingData.adults * SESSION_PRICES[bookingData.duration].adult + bookingData.children * SESSION_PRICES[bookingData.duration].child).toFixed(2)}
                                        </span>
                                      </p>
                                    </div>
                                  )}
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Special Requests (Optional)
                                    </label>
                                    <Input
                                      placeholder="Any special requirements?"
                                      value={bookingData.special_requests}
                                      onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
                                    />
                                  </div>
                                </div>
                                
                                <Button 
                                  className="w-full bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 disabled:opacity-50"
                                  onClick={async () => {
                                    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone || !bookingData.duration) {
                                      toast.error('Please fill in all required fields');
                                      return;
                                    }
                                    
                                    if (bookingData.adults + bookingData.children < 1) {
                                      toast.error('Please enter at least one participant');
                                      return;
                                    }
                                    
                                    setIsBooking(true);
                                    
                                    try {
                                      const payload = {
                                        event_id: selectedEvent.id,
                                        event_title: selectedEvent.title,
                                        event_date: parseLocalDate(selectedEvent.date).toLocaleDateString(),
                                        event_time: selectedEvent.time,
                                        customer_name: bookingData.customer_name,
                                        customer_email: bookingData.customer_email,
                                        customer_phone: bookingData.customer_phone,
                                        participants: bookingData.adults + bookingData.children,
                                        adults: bookingData.adults,
                                        children: bookingData.children,
                                        duration: bookingData.duration === '30min' ? '30 Minutes' : '1 Hour',
                                        special_requests: bookingData.special_requests
                                      };
                                      
                                      const response = await axios.post(`${API_URL}/api/bookings`, payload);
                                      
                                      if (response.data.success) {
                                        // Show success modal
                                        setBookingConfirmation({
                                          eventTitle: selectedEvent.title,
                                          date: parseLocalDate(selectedEvent.date).toLocaleDateString('en-US', { 
                                            weekday: 'long', 
                                            year: 'numeric', 
                                            month: 'long', 
                                            day: 'numeric' 
                                          }),
                                          time: selectedEvent.time,
                                          duration: payload.duration,
                                          participants: `${bookingData.adults + bookingData.children} (${bookingData.adults} Adult${bookingData.adults !== 1 ? 's' : ''}, ${bookingData.children} Child${bookingData.children !== 1 ? 'ren' : ''})`,
                                          email: bookingData.customer_email,
                                          confirmationNumber: response.data.confirmation_number,
                                          estimatedTotal: response.data.estimated_total
                                        });
                                        setShowSuccessModal(true);
                                        
                                        setBookingData({
                                          customer_name: '',
                                          customer_email: '',
                                          customer_phone: '',
                                          adults: 1,
    children: 0,
                                          duration: '30min',
                                          special_requests: ''
                                        });
                                        setSelectedEvent(null);
                                      } else {
                                        toast.error('Booking failed', {
                                          description: response.data.message || 'Please try again later.'
                                        });
                                      }
                                    } catch (error) {
                                      toast.error('Booking failed', {
                                        description: 'Please try again later or contact us directly.'
                                      });
                                    } finally {
                                      setIsBooking(false);
                                    }
                                  }}
                                  disabled={isBooking}
                                >
                                  {isBooking ? 'Booking...' : 'Confirm Booking'}
                                </Button>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </section>

      {/* Past Events Section */}
      {pastEvents && pastEvents.length > 0 && (
        <section className="px-6 py-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <h3 className="text-xl font-bold mb-4 text-gray-700">Past Events</h3>
            <div className="space-y-4">
              {pastEvents.map((event) => (
                <div 
                  key={event.id}
                  className="bg-white p-4 rounded-xl shadow-md border-l-4 border-gray-400 opacity-75"
                >
                  <div className="mb-2">
                    <span className="inline-block px-3 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-full">
                      Completed Event
                    </span>
                  </div>
                  
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-start gap-2.5 mb-2.5">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CalendarIcon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-700 mb-0.5">
                            {event.title}
                          </h4>
                          <p className="text-gray-600 text-xs">{event.description}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <CalendarIcon className="w-3.5 h-3.5 text-gray-500" />
                          <span>{parseLocalDate(event.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-gray-500" />
                          <span>{event.time}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                          <Clock className="w-3.5 h-3.5 text-gray-500" />
                          <span>{event.duration}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-green-600 font-semibold">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                          <span>Completed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Custom Booking Dialog (for direct session bookings) */}
      {showCustomBookingDialog && (
      <Dialog open={true} onOpenChange={(open) => {
        if (!open) {
          setShowCustomBookingDialog(false);
          setSelectedTimeSlot(null);
          setSelectedDuration(null);
        }
      }}>
        <DialogContent className="sm:max-w-md" aria-describedby="custom-booking-dialog-description">
          <DialogHeader>
            <DialogTitle>Book Your {selectedDuration === '30min' ? '30-Minute' : '1-Hour'} Session</DialogTitle>
            <DialogDescription id="custom-booking-dialog-description">
              Complete your booking details below
            </DialogDescription>
          </DialogHeader>
          
          {/* Show selected date and time prominently */}
          <div className="bg-gradient-to-r from-pink-50 to-yellow-50 p-4 rounded-lg border-2 border-pink-200 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">📅</span>
              <h3 className="font-semibold text-gray-900">Your Selected Time:</h3>
            </div>
            <div className="space-y-1">
              <p className="text-gray-700"><strong>Date:</strong> {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              <p className="text-gray-700"><strong>Time:</strong> {selectedTimeSlot}</p>
              <p className="text-gray-700"><strong>Duration:</strong> {selectedDuration === '30min' ? '30 minutes' : '1 hour'}</p>
              {SESSION_PRICES[selectedDuration] && (
                <p className="text-gray-900 pt-1 border-t border-pink-200 mt-2" data-testid="custom-booking-estimated-total">
                  <strong>Estimated Total:</strong>{' '}
                  <span className="font-black text-pink-600 text-lg">
                    ${(bookingData.adults * SESSION_PRICES[selectedDuration].adult + bookingData.children * SESSION_PRICES[selectedDuration].child).toFixed(2)}
                  </span>
                  <span className="text-xs text-gray-500 block">
                    {bookingData.adults} adult(s) × ${SESSION_PRICES[selectedDuration].adult} + {bookingData.children} child(ren) × ${SESSION_PRICES[selectedDuration].child}
                  </span>
                </p>
              )}
            </div>
          </div>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <Input
                placeholder="Your full name"
                value={bookingData.customer_name}
                onChange={(e) => setBookingData({...bookingData, customer_name: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email *</label>
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={bookingData.customer_email}
                onChange={(e) => setBookingData({...bookingData, customer_email: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Phone *</label>
              <Input
                type="tel"
                placeholder="(701) 123-4567"
                value={bookingData.customer_phone}
                onChange={(e) => setBookingData({...bookingData, customer_phone: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Number of Participants</label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Adults</label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="custom-booking-adults-input"
                    value={bookingData.adults}
                    onChange={(e) => setBookingData({...bookingData, adults: Math.max(0, parseInt(e.target.value) || 0)})}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Children</label>
                  <Input
                    type="number"
                    min="0"
                    data-testid="custom-booking-children-input"
                    value={bookingData.children}
                    onChange={(e) => setBookingData({...bookingData, children: Math.max(0, parseInt(e.target.value) || 0)})}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Special Requests (Optional)</label>
              <Input
                placeholder="Any special requirements?"
                value={bookingData.special_requests}
                onChange={(e) => setBookingData({...bookingData, special_requests: e.target.value})}
              />
            </div>
            <Button 
              onClick={handleBookingSubmit}
              disabled={isBooking}
              className="w-full bg-gradient-to-r from-pink-500 to-yellow-500 hover:from-pink-600 hover:to-yellow-600"
            >
              {isBooking ? 'Processing...' : 'Confirm Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      )}
      
      {/* Past Event Popup Dialog Removed - May 16th event moved to past events */}
      
      {/* Success Modal - Large Centered */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-lg">
          <div className="text-center py-6">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Booking Confirmed!
            </h2>
            
            {bookingConfirmation?.confirmationNumber && (
              <div className="bg-gradient-to-r from-yellow-50 to-pink-50 border-2 border-dashed border-pink-400 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">Your Confirmation Number</p>
                <p className="text-2xl font-black tracking-widest text-pink-600" data-testid="booking-confirmation-number">
                  {bookingConfirmation.confirmationNumber}
                </p>
              </div>
            )}
            
            <div className="bg-gradient-to-br from-pink-50 to-yellow-50 p-6 rounded-xl mb-4">
              <div className="space-y-3 text-left">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Event:</p>
                  <p className="text-lg font-bold text-gray-900">{bookingConfirmation?.eventTitle}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date:</p>
                    <p className="font-semibold text-gray-900">{bookingConfirmation?.date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Time:</p>
                    <p className="font-semibold text-gray-900">{bookingConfirmation?.time}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Duration:</p>
                    <p className="font-semibold text-gray-900">{bookingConfirmation?.duration}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Participants:</p>
                    <p className="font-semibold text-gray-900">{bookingConfirmation?.participants}</p>
                  </div>
                </div>
                
                {bookingConfirmation?.estimatedTotal != null && (
                  <div className="pt-3 border-t border-pink-200">
                    <p className="text-sm text-gray-600 mb-1">Estimated Total (payable at the studio):</p>
                    <p className="font-black text-2xl text-pink-600" data-testid="booking-estimated-total">
                      ${Number(bookingConfirmation.estimatedTotal).toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>✓ What happens next?</strong>
              </p>
              <p className="text-sm text-blue-700 mt-2">
                • A confirmation email with your confirmation number is on its way to <strong>{bookingConfirmation?.email}</strong>
              </p>
              <p className="text-sm text-blue-700">
                • We've been notified at info@creativeminds-usa.com and will contact you to finalize your reservation
              </p>
              <p className="text-sm text-blue-700">
                • Arrive 10 minutes early, wear clothes you don't mind getting paint on - we provide all materials & protective gear!
              </p>
            </div>
            
            <Button
              onClick={() => setShowSuccessModal(false)}
              className="w-full bg-gradient-to-r from-pink-500 to-yellow-500 hover:from-pink-600 hover:to-yellow-600 text-white font-bold py-3 text-lg"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Birthday Party Inquiry Dialog */}
      <PartyInquiryDialog open={showPartyDialog} onOpenChange={setShowPartyDialog} />
      
      {/* Team Building Inquiry Dialog */}
      <TeamInquiryDialog open={showTeamDialog} onOpenChange={setShowTeamDialog} />
    </div>
  );
};
